// Каркас: вход, боковое меню на ПК, нижняя навигация на телефоне, полоса «нет связи».

import { useState, type ReactNode } from "react";
import { Api, errorText } from "./api";
import { Link, useRoute } from "./router";
import { Button, Field, Icon, Input, NetBar } from "./ui";

export type NavItem = { to: string; label: string; icon: string; mobile?: boolean; exact?: boolean };

export function Shell({ brand, nav, user, onLogout, online, queued, children }: {
  brand: string; nav: NavItem[]; user: string; onLogout: () => void; online: boolean; queued?: number; children: ReactNode;
}) {
  const r = useRoute();
  const exact = new Set(nav.filter((n) => n.exact).map((n) => n.to));
  const active = (to: string) => r.path === to || (!exact.has(to) && to !== "/" && r.path.startsWith(to + "/"));
  return (
    <div className="shell">
      <nav className="rail" aria-label="Разделы">
        <div className="brand">
          <svg width="26" height="26" viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="6" fill="#1F5FBF" /><path d="M7 22h18M9 22V12l7-4 7 4v10" stroke="white" strokeWidth="2.4" fill="none" strokeLinejoin="round" /></svg>
          <span>{brand}</span>
        </div>
        {nav.map((n) => (
          <Link key={n.to} to={n.to} className={active(n.to) ? "active" : ""} aria-current={active(n.to) ? "page" : undefined}>
            <Icon name={n.icon} />
            {n.label}
          </Link>
        ))}
        <div className="spacer" />
        <div className="who">
          <div>{user}</div>
          <button onClick={onLogout}>Выйти</button>
        </div>
      </nav>
      <div className="main">
        <NetBar online={online} queued={queued} />
        {children}
      </div>
      <nav className="bottom-nav" aria-label="Разделы">
        {nav.filter((n) => n.mobile).map((n) => (
          <Link key={n.to} to={n.to} className={active(n.to) ? "active" : ""}>
            <Icon name={n.icon} />
            {n.label}
          </Link>
        ))}
        {/* на телефоне боковой панели нет: выход нужен, чтобы на общий телефон мог войти сменщик */}
        <button type="button" title={`Выйти: ${user}`} onClick={onLogout}>
          <Icon name="logout" />
          Выйти
        </button>
      </nav>
    </div>
  );
}

export function TopBar({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="topbar">
      <h1>{title}</h1>
      <div className="row grow" style={{ justifyContent: "flex-end" }}>{children}</div>
    </div>
  );
}

/** Метка сборки из index.html: по ней видно, что после обновления браузер открыл новую версию. */
const BUILD = document.querySelector<HTMLMetaElement>('meta[name="build"]')?.content ?? "разработка";

type LoginMode = "password" | "pin" | "driver";
const MODE_KEY = "rbs.loginMode";

function readMode(allowDriver?: boolean): LoginMode {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === "driver" && allowDriver) return "driver";
  } catch { /* без памяти */ }
  return "password";
}

/** Телефон водителя к виду логина: 79161234567. */
function phoneLogin(v: string): string {
  const d = v.replace(/\D/g, "");
  if (d.length === 10) return "7" + d;
  if (d.length === 11 && d[0] === "8") return "7" + d.slice(1);
  return d;
}

export function LoginScreen({ api, title, subtitle, onDone, allowPin, allowDriver }: {
  api: Api; title: string; subtitle: string; onDone: () => void; allowPin?: boolean; allowDriver?: boolean;
}) {
  const [mode, setModeState] = useState<LoginMode>(() => readMode(allowDriver));
  const setMode = (m: LoginMode) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* без памяти */ }
  };
  const [login, setLogin] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "pin") await api.loginPin(login, secret);
      else if (mode === "driver") await api.loginPin(phoneLogin(login), secret.trim());
      else await api.login(login, secret);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form className="box" onSubmit={submit}>
        <div className="mark">
          <svg width="36" height="36" viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="6" fill="#1F5FBF" /><path d="M7 22h18M9 22V12l7-4 7 4v10" stroke="white" strokeWidth="2.4" fill="none" strokeLinejoin="round" /></svg>
          <div>
            <h1>{title}</h1>
            <p className="muted small">{subtitle}</p>
          </div>
        </div>
        {mode === "driver" && <p className="small muted" style={{ margin: 0 }}>Вход для водителя: ваш номер телефона и код, который дал диспетчер.</p>}
        <Field label={mode === "driver" ? "Телефон" : "Логин"}>
          <Input autoFocus autoComplete={mode === "driver" ? "tel" : "username"} type={mode === "driver" ? "tel" : "text"}
            placeholder={mode === "driver" ? "+7 900 000-00-00" : undefined} value={login} onChange={(e) => setLogin(e.target.value)} />
        </Field>
        <Field label={mode === "pin" ? "PIN-код" : mode === "driver" ? "Код" : "Пароль"}>
          <Input type="password" inputMode={mode === "password" ? undefined : "numeric"} autoComplete="current-password" value={secret} onChange={(e) => setSecret(e.target.value)} />
        </Field>
        {error && <div className="alert risk" role="alert"><div className="t">{error}</div></div>}
        <Button type="submit" variant="primary" disabled={busy || !login || !secret}>Войти</Button>
        {allowPin && mode !== "driver" && (
          <Button variant="ghost" onClick={() => { setMode(mode === "pin" ? "password" : "pin"); setSecret(""); }}>
            {mode === "pin" ? "Войти по паролю" : "Быстрая смена за общим ПК: войти по PIN"}
          </Button>
        )}
        {allowDriver && (
          <Button variant="ghost" onClick={() => { setMode(mode === "driver" ? "password" : "driver"); setSecret(""); setLogin(""); }}>
            {mode === "driver" ? "Я сотрудник: войти по логину" : "Я водитель: войти по телефону"}
          </Button>
        )}
        <p className="muted small" style={{ textAlign: "center", margin: 0 }}>Версия {BUILD}</p>
      </form>
    </div>
  );
}

export function Placeholder({ title }: { title: string }) {
  return <div className="content"><h1>{title}</h1></div>;
}
